import { makeAutoObservable } from "mobx";
import RootStore from "./RootStore";

// Interface defining the structure of a single point on our canvas.
// This ensures every point has a consistent shape.
export interface Point {
  x: number;
  y: number;
  pressure?: number; // Optional pressure for stylus support
  timestamp?: number; // For stroke smoothing
}

// Stroke configuration interface
export interface StrokeConfig {
  color: string;
  width: number;
  opacity: number;
}

// A Path is an array of points, representing a single continuous line.
export interface Path {
  points: Point[];
  config: StrokeConfig;
  id: string;
}

// Configuration for the signature pad
export interface SignaturePadConfig {
  defaultStroke: StrokeConfig;
  smoothing: boolean;
  velocityFilterWeight: number;
  minWidth: number;
  maxWidth: number;
}

class SignaturePadStore {
  rootStore: RootStore;

  // observable: This property will be tracked by MobX.
  // It's an array of paths, where each path contains points and styling info.
  paths: Path[] = [];

  // observable: A boolean flag to track if the user is currently drawing.
  isDrawing: boolean = false;

  // observable: History for undo/redo functionality
  private history: Path[][] = [];
  private historyIndex: number = -1;

  // observable: Current stroke configuration
  currentStrokeConfig: StrokeConfig = {
    color: "#000000",
    width: 4,
    opacity: 1,
  };

  // Configuration for the signature pad
  config: SignaturePadConfig = {
    defaultStroke: { color: "#000000", width: 4, opacity: 1 },
    smoothing: true,
    velocityFilterWeight: 0.7,
    minWidth: 0.5,
    maxWidth: 2.5,
  };

  // Cache for SVG path strings to avoid recalculation
  private pathCache: Map<string, string> = new Map();

  constructor(rootStore: RootStore) {
    // makeObservable links the class properties to MobX's reactivity system.
    makeAutoObservable(this, {
      rootStore: false, // Prevents the rootStore from being observable.
      config: false, // Configuration is usually static
    });
    this.rootStore = rootStore;

    // Initialize with empty history - will be populated when strokes are completed
    this.history = [];
    this.historyIndex = -1;
  }

  // Utility method to generate unique IDs for paths
  private generatePathId = (): string => {
    return `path_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Save current state to history for undo/redo
  private saveToHistory = () => {
    // Remove any history after current index (for when we're in the middle of history)
    this.history = this.history.slice(0, this.historyIndex + 1);

    // Add current state to history
    this.history.push(JSON.parse(JSON.stringify(this.paths)));

    // Limit history size to prevent memory issues
    if (this.history.length > 50) {
      this.history = this.history.slice(-50);
    }

    this.historyIndex = this.history.length - 1;
  };

  // Calculate distance between two points
  private getDistance = (point1: Point, point2: Point): number => {
    return Math.sqrt(
      Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2)
    );
  };

  // Smooth stroke using velocity-based filtering
  private smoothPoint = (point: Point, previousPoint?: Point): Point => {
    if (!this.config.smoothing || !previousPoint) {
      return point;
    }

    const distance = this.getDistance(point, previousPoint);
    const velocity =
      distance / (point.timestamp! - previousPoint.timestamp! || 1);

    // Adjust stroke width based on velocity
    const dynamicWidth = Math.max(
      this.config.minWidth,
      Math.min(
        this.config.maxWidth,
        this.currentStrokeConfig.width / (velocity * 0.1 + 1)
      )
    );

    return {
      ...point,
      pressure: dynamicWidth / this.config.maxWidth,
    };
  };

  // action: Starts a new path with the given point.
  startDrawing = (point: Point) => {
    this.isDrawing = true;

    const enhancedPoint: Point = {
      ...point,
      timestamp: Date.now(),
      pressure: point.pressure || 1,
    };

    const newPath: Path = {
      points: [enhancedPoint],
      config: { ...this.currentStrokeConfig },
      id: this.generatePathId(),
    };

    this.paths.push(newPath);
    this.pathCache.clear(); // Clear cache when paths change
  };

  // action: Adds a new point to the most recent path.
  draw = (point: Point) => {
    if (!this.isDrawing || this.paths.length === 0) return;

    const currentPath = this.paths[this.paths.length - 1];
    const lastPoint = currentPath.points[currentPath.points.length - 1];

    // Add timestamp for smoothing calculations
    const enhancedPoint: Point = {
      ...point,
      timestamp: Date.now(),
      pressure: point.pressure || 1,
    };

    // Apply smoothing if enabled
    const smoothedPoint = this.smoothPoint(enhancedPoint, lastPoint);

    // Only add point if it's far enough from the last point (reduces redundant points)
    const distance = this.getDistance(smoothedPoint, lastPoint);
    if (distance > 1) {
      currentPath.points.push(smoothedPoint);

      // Clear cache for this specific path
      this.pathCache.delete(currentPath.id);
    }
  };

  // action: Stops the current drawing session.
  endDrawing = () => {
    if (this.isDrawing) {
      this.saveToHistory(); // Save state after completing the stroke
      this.isDrawing = false;
    }
  };

  // action: Clears all paths from the signature, effectively resetting the canvas.
  clearSignature = () => {
    this.paths = [];
    this.pathCache.clear();
    // Reset history completely
    this.history = [];
    this.historyIndex = -1;
  };

  // action: Undo the last action
  undo = () => {
    if (this.historyIndex >= 0) {
      this.historyIndex--;
      if (this.historyIndex >= 0) {
        this.paths = JSON.parse(
          JSON.stringify(this.history[this.historyIndex])
        );
      } else {
        this.paths = []; // No more history, restore to empty state
      }
      this.pathCache.clear();
    }
  };

  // action: Redo the next action
  redo = () => {
    if (this.historyIndex < this.history.length - 1) {
      this.historyIndex++;
      this.paths = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
      this.pathCache.clear();
    }
  };

  // action: Update stroke configuration
  updateStrokeConfig = (config: Partial<StrokeConfig>) => {
    this.currentStrokeConfig = { ...this.currentStrokeConfig, ...config };
  };

  // action: Update pad configuration
  updateConfig = (config: Partial<SignaturePadConfig>) => {
    this.config = { ...this.config, ...config };
  };

  // computed: Check if there are any paths (signature exists)
  get hasSignature(): boolean {
    return (
      this.paths.length > 0 && this.paths.some((path) => path.points.length > 0)
    );
  }

  // computed: Check if undo is available
  get canUndo(): boolean {
    return this.historyIndex >= 0;
  }

  // computed: Check if redo is available
  get canRedo(): boolean {
    return this.historyIndex < this.history.length - 1;
  }

  // computed: Check if current stroke config matches default values
  get isAtDefaultConfig(): boolean {
    return (
      this.currentStrokeConfig.color === this.config.defaultStroke.color &&
      this.currentStrokeConfig.width === this.config.defaultStroke.width &&
      this.currentStrokeConfig.opacity === this.config.defaultStroke.opacity
    );
  }

  // computed: Get signature bounds for export
  get signatureBounds(): {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  } | null {
    if (!this.hasSignature) return null;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    this.paths.forEach((path) => {
      path.points.forEach((point) => {
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
      });
    });

    return { minX, minY, maxX, maxY };
  }

  // Helper method to generate SVG path data for a single path
  private generatePathData = (path: Path): string => {
    if (path.points.length === 0) return "";

    // Check cache first
    if (this.pathCache.has(path.id)) {
      return this.pathCache.get(path.id)!;
    }

    let pathData = "";
    const points = path.points;

    if (points.length === 1) {
      // Single point - draw a small circle
      const point = points[0];
      pathData = `M ${point.x - 0.5} ${point.y} A 0.5 0.5 0 1 0 ${
        point.x + 0.5
      } ${point.y} A 0.5 0.5 0 1 0 ${point.x - 0.5} ${point.y}`;
    } else {
      // Multiple points - create smooth curve
      pathData = `M ${points[0].x} ${points[0].y}`;

      if (this.config.smoothing && points.length > 2) {
        // Use quadratic curves for smoother lines
        for (let i = 1; i < points.length - 1; i++) {
          const current = points[i];
          const next = points[i + 1];
          const controlX = (current.x + next.x) / 2;
          const controlY = (current.y + next.y) / 2;
          pathData += ` Q ${current.x} ${current.y} ${controlX} ${controlY}`;
        }
        // Final point
        const lastPoint = points[points.length - 1];
        pathData += ` T ${lastPoint.x} ${lastPoint.y}`;
      } else {
        // Simple line segments
        for (let i = 1; i < points.length; i++) {
          pathData += ` L ${points[i].x} ${points[i].y}`;
        }
      }
    }

    // Cache the result
    this.pathCache.set(path.id, pathData);
    return pathData;
  };

  // computed: This getter will re-evaluate automatically whenever its dependencies (paths) change.
  // It efficiently calculates the SVG path data string.
  get svgPaths(): string {
    return this.paths
      .map((path) => this.generatePathData(path))
      .filter((pathData) => pathData.length > 0)
      .join(" ");
  }

  // Method to export signature as data URL
  exportAsDataURL = (
    format: "png" | "jpeg" = "png",
    quality = 0.92
  ): string | null => {
    if (!this.hasSignature) return null;

    const bounds = this.signatureBounds;
    if (!bounds) return null;

    // Create a temporary canvas
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // Set canvas size with some padding
    const padding = 10;
    canvas.width = bounds.maxX - bounds.minX + padding * 2;
    canvas.height = bounds.maxY - bounds.minY + padding * 2;

    // Set white background
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw signature paths
    this.paths.forEach((path) => {
      if (path.points.length === 0) return;

      ctx.beginPath();
      ctx.strokeStyle = path.config.color;
      ctx.lineWidth = path.config.width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.globalAlpha = path.config.opacity;

      const firstPoint = path.points[0];
      ctx.moveTo(
        firstPoint.x - bounds.minX + padding,
        firstPoint.y - bounds.minY + padding
      );

      for (let i = 1; i < path.points.length; i++) {
        const point = path.points[i];
        ctx.lineTo(
          point.x - bounds.minX + padding,
          point.y - bounds.minY + padding
        );
      }

      ctx.stroke();
    });

    return canvas.toDataURL(`image/${format}`, quality);
  };
}

// Export a singleton instance of the store. This ensures the entire app
// uses the same instance for state management.

export default SignaturePadStore;
